import { createElementObservation } from "@/vendors/Snowplow/observation/observation-boundary-utils"
import type { ElementObservationsAPI } from "@/vendors/Snowplow/types/observation-boundary-tracker.types"
import { isHTMLElement } from "@/vendors/Snowplow/utilities/is-html-element"

type BoundaryObserverCallbacks = {
  readonly onEnter: () => void
  readonly onExit: () => void
}

type ObserverRefs = {
  readonly element: IntersectionObserver | null
}

/** Fires immediately when any part of the boundary becomes visible. */
const boundaryThreshold = 0

/**
 * Multiple thresholds to capture maximum visible pixels accurately.
 * Fires at 0%, 10%, 25%, 50%, 75%, and 100% visibility.
 */
// biome-ignore lint/style/noMagicNumbers: Visibility thresholds are self-documenting
const elementVisibilityThresholds = [0, 0.1, 0.25, 0.5, 0.75, 1.0] as const

/**
 * Creates an IntersectionObserver that tracks when the boundary container
 * enters or exits the viewport.
 *
 * Uses internal state to prevent duplicate enter/exit callbacks when
 * the intersection ratio changes but visibility state doesn't.
 *
 */
export function createBoundaryObserver(
  callbacks: BoundaryObserverCallbacks,
): IntersectionObserver {
  const state = { isVisible: false }

  return new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && !state.isVisible) {
        state.isVisible = true
        callbacks.onEnter()
      } else if (!entry.isIntersecting && state.isVisible) {
        state.isVisible = false
        callbacks.onExit()
      }
    },
    { threshold: boundaryThreshold },
  )
}

/**
 * Creates an IntersectionObserver that tracks individual elements within
 * the boundary, measuring visibility time and maximum visible area.
 *
 * Uses multiple thresholds to capture accurate "max pixels in view" data
 * as elements scroll through the viewport.
 *
 * @param api - The element observations API for tracking state
 * @param getIsPageVisible - Function returning current page visibility state
 *
 */
export function createElementObserver(
  api: ElementObservationsAPI,
  getIsPageVisible: () => boolean,
): IntersectionObserver {
  return new IntersectionObserver(
    (entries) => {
      const isVisible = getIsPageVisible()
      const now = new Date()
      const mark = performance.now()

      for (const entry of entries) {
        processElementEntry(api, entry, isVisible, now, mark)
      }
    },
    { threshold: [...elementVisibilityThresholds] },
  )
}

/**
 * Processes a single intersection observer entry, updating tracking state
 * based on visibility changes.
 *
 * - Starts tracking when element becomes visible (and page is visible)
 * - Updates metrics (pixels, dimensions) while element is intersecting
 * - Pauses tracking when element leaves viewport
 */
function processElementEntry(
  api: ElementObservationsAPI,
  entry: IntersectionObserverEntry,
  isPageVisible: boolean,
  now: Date,
  mark: number,
): void {
  const observation = api.getElement(entry.target)

  if (!observation) {
    return
  }

  if (entry.isIntersecting && isPageVisible) {
    api.startTracking(entry.target, now, mark)
  }

  if (entry.isIntersecting) {
    updateElementMetrics(api, entry)
  }

  if (!entry.isIntersecting && observation.isCurrentlyVisible) {
    api.pauseTracking(entry.target, now, mark)
  }
}

/**
 * Updates the maximum visible pixels and dimensions for an element.
 * Called at each intersection threshold to capture the peak values.
 */
function updateElementMetrics(
  api: ElementObservationsAPI,
  entry: IntersectionObserverEntry,
): void {
  const visiblePixels =
    entry.intersectionRect.width * entry.intersectionRect.height

  api.updateVisiblePixels(entry.target, visiblePixels)
  api.updateDimensions(
    entry.target,
    entry.boundingClientRect.width,
    entry.boundingClientRect.height,
  )
}

/**
 * Creates a MutationObserver that watches for dynamically added or removed
 * elements within the boundary.
 *
 * When elements with `data-obs-element` are added to the DOM, they are
 * automatically registered for observation. When removed, their tracking
 * is paused and they are unobserved.
 *
 * @param api - The element observations API for tracking state
 * @param getObservers - Function returning current observer refs (for null safety)
 *
 */
export function createMutationObserver(
  api: ElementObservationsAPI,
  getObservers: () => ObserverRefs,
): MutationObserver {
  return new MutationObserver((mutations) => {
    const observers = getObservers()

    if (!observers.element) {
      return
    }

    for (const mutation of mutations) {
      if (mutation.type !== "childList") {
        continue
      }

      handleAddedNodes(mutation.addedNodes, api, observers.element)
      handleRemovedNodes(mutation.removedNodes, api, observers.element)
    }
  })
}

/**
 * Registers an element for observation tracking.
 *
 * Reads data attributes from the element to create an observation record,
 * then starts observing it with the IntersectionObserver.
 *
 * Required data attributes:
 * - `data-obs-element` - Marker attribute (presence required)
 * - `data-obs-element-id` - Unique element identifier
 * - `data-obs-element-content` - Content identifier for analytics
 * - `data-obs-element-type` - Optional element type category
 */
export function observeElement(
  element: HTMLElement,
  api: ElementObservationsAPI,
  elementObserver: IntersectionObserver,
): void {
  const observation = createElementObservation(element)

  if (!observation) {
    return
  }

  api.addElement(element, observation)
  elementObserver.observe(element)
}

/**
 * Processes added DOM nodes, finding and observing any elements
 * marked with `data-obs-element`.
 *
 * Handles both direct additions and nested children.
 */
function handleAddedNodes(
  nodes: NodeList,
  api: ElementObservationsAPI,
  elementObserver: IntersectionObserver | null,
): void {
  if (!elementObserver) {
    return
  }

  for (const node of nodes) {
    if (!isHTMLElement(node)) {
      continue
    }

    if (node.hasAttribute("data-obs-element")) {
      observeElement(node, api, elementObserver)
    }

    const children = node.querySelectorAll<HTMLElement>("[data-obs-element]")

    for (const child of children) {
      observeElement(child, api, elementObserver)
    }
  }
}

/**
 * Processes removed DOM nodes, pausing tracking and unobserving any
 * elements marked with `data-obs-element`.
 *
 * Ensures time-in-view is calculated before removal.
 */
function handleRemovedNodes(
  nodes: NodeList,
  api: ElementObservationsAPI,
  elementObserver: IntersectionObserver | null,
): void {
  if (!elementObserver) {
    return
  }

  const now = new Date()
  const mark = performance.now()

  for (const node of nodes) {
    if (!isHTMLElement(node)) {
      continue
    }

    if (node.hasAttribute("data-obs-element")) {
      unObserveElement(node, api, elementObserver, now, mark)
    }

    const children = node.querySelectorAll<HTMLElement>("[data-obs-element]")

    for (const child of children) {
      unObserveElement(child, api, elementObserver, now, mark)
    }
  }
}

/**
 * Stops observing an element and finalizes its tracking data.
 *
 * The element remains in the tracking map so its data is included
 * in the next boundary exit event. Cleanup of detached elements
 * happens via resetAfterSend.
 *
 * Order matters:
 * 1. Pause tracking (calculates final time-in-view)
 * 2. Unobserve (stops intersection callbacks)
 */
function unObserveElement(
  element: HTMLElement,
  api: ElementObservationsAPI,
  elementObserver: IntersectionObserver,
  now: Date,
  mark: number,
): void {
  api.pauseTracking(element, now, mark)
  elementObserver.unobserve(element)
}
